<template>
  <div :class="t.wrapperLoose">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>

    <!-- Address Fields -->
    <div class="space-y-4">
      <!-- Street Address -->
      <div>
        <label :class="t.subLabel">Street Address</label>
        <input
          v-model="address.line1"
          type="text"
          autocomplete="address-line1"
          :class="[t.input, errors.line1 ? 'border-red-500' : 'border-gray-300']"
          :style="focusedField === 'line1' ? focusStyle : {}"
          @focus="focusedField = 'line1'"
          @blur="focusedField = null; validateField('line1')"
          placeholder="123 Main St"
        />
      </div>

      <!-- Address Line 2 -->
      <div>
        <label :class="t.subLabel">Apt, Suite, etc. (optional)</label>
        <input
          v-model="address.line2"
          type="text"
          autocomplete="address-line2"
          :class="[t.input, 'border-gray-300']"
          :style="focusedField === 'line2' ? focusStyle : {}"
          @focus="focusedField = 'line2'"
          @blur="focusedField = null"
          placeholder="Apt 4B"
        />
      </div>

      <!-- City -->
      <div>
        <label :class="t.subLabel">City</label>
        <input
          v-model="address.city"
          type="text"
          autocomplete="address-level2"
          :class="[t.input, errors.city ? 'border-red-500' : 'border-gray-300']"
          :style="focusedField === 'city' ? focusStyle : {}"
          @focus="focusedField = 'city'"
          @blur="focusedField = null; validateField('city')"
          placeholder="City"
        />
      </div>

      <!-- State and Zip Row -->
      <div class="flex gap-4">
        <!-- State -->
        <div class="flex-1">
          <label :class="t.subLabel">State</label>
          <select
            v-model="address.state"
            autocomplete="address-level1"
            :class="[t.input, 'appearance-none bg-white', errors.state ? 'border-red-500' : 'border-gray-300']"
            :style="focusedField === 'state' ? focusStyle : {}"
            @focus="focusedField = 'state'"
            @blur="focusedField = null; validateField('state')"
          >
            <option value="">Select...</option>
            <option v-for="state in states" :key="state.abbr" :value="state.abbr">
              {{ state.name }}
            </option>
          </select>
        </div>

        <!-- Zip -->
        <div class="w-1/3">
          <label :class="t.subLabel">Zip</label>
          <input
            v-model="address.zip"
            type="text"
            inputmode="numeric"
            autocomplete="postal-code"
            maxlength="10"
            :class="[t.input, errors.zip ? 'border-red-500' : 'border-gray-300']"
            :style="focusedField === 'zip' ? focusStyle : {}"
            @focus="focusedField = 'zip'"
            @blur="focusedField = null; validateField('zip')"
            placeholder="12345"
          />
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <p v-if="error" :class="t.error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { tokensFor } from './size_tokens';

export default {
  name: 'QuestionAddress',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      originalAddress: null,
      error: false,
      errorMessage: 'Please complete the required address fields.',
      errors: {
        line1: false,
        city: false,
        state: false,
        zip: false,
      },
      focusedField: null,
      states: [
        { abbr: 'AL', name: 'Alabama' },
        { abbr: 'AK', name: 'Alaska' },
        { abbr: 'AZ', name: 'Arizona' },
        { abbr: 'AR', name: 'Arkansas' },
        { abbr: 'CA', name: 'California' },
        { abbr: 'CO', name: 'Colorado' },
        { abbr: 'CT', name: 'Connecticut' },
        { abbr: 'DE', name: 'Delaware' },
        { abbr: 'FL', name: 'Florida' },
        { abbr: 'GA', name: 'Georgia' },
        { abbr: 'HI', name: 'Hawaii' },
        { abbr: 'ID', name: 'Idaho' },
        { abbr: 'IL', name: 'Illinois' },
        { abbr: 'IN', name: 'Indiana' },
        { abbr: 'IA', name: 'Iowa' },
        { abbr: 'KS', name: 'Kansas' },
        { abbr: 'KY', name: 'Kentucky' },
        { abbr: 'LA', name: 'Louisiana' },
        { abbr: 'ME', name: 'Maine' },
        { abbr: 'MD', name: 'Maryland' },
        { abbr: 'MA', name: 'Massachusetts' },
        { abbr: 'MI', name: 'Michigan' },
        { abbr: 'MN', name: 'Minnesota' },
        { abbr: 'MS', name: 'Mississippi' },
        { abbr: 'MO', name: 'Missouri' },
        { abbr: 'MT', name: 'Montana' },
        { abbr: 'NE', name: 'Nebraska' },
        { abbr: 'NV', name: 'Nevada' },
        { abbr: 'NH', name: 'New Hampshire' },
        { abbr: 'NJ', name: 'New Jersey' },
        { abbr: 'NM', name: 'New Mexico' },
        { abbr: 'NY', name: 'New York' },
        { abbr: 'NC', name: 'North Carolina' },
        { abbr: 'ND', name: 'North Dakota' },
        { abbr: 'OH', name: 'Ohio' },
        { abbr: 'OK', name: 'Oklahoma' },
        { abbr: 'OR', name: 'Oregon' },
        { abbr: 'PA', name: 'Pennsylvania' },
        { abbr: 'RI', name: 'Rhode Island' },
        { abbr: 'SC', name: 'South Carolina' },
        { abbr: 'SD', name: 'South Dakota' },
        { abbr: 'TN', name: 'Tennessee' },
        { abbr: 'TX', name: 'Texas' },
        { abbr: 'UT', name: 'Utah' },
        { abbr: 'VT', name: 'Vermont' },
        { abbr: 'VA', name: 'Virginia' },
        { abbr: 'WA', name: 'Washington' },
        { abbr: 'WV', name: 'West Virginia' },
        { abbr: 'WI', name: 'Wisconsin' },
        { abbr: 'WY', name: 'Wyoming' },
        { abbr: 'DC', name: 'District of Columbia' },
      ],
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    focusStyle() {
      return {
        borderColor: 'var(--team-primary)',
        boxShadow: '0 0 0 3px rgba(var(--team-primary-rgb, 55, 65, 81), 0.1)',
      };
    },
    dirty() {
      if (!this.originalAddress) return this.hasContent;
      return (
        this.address.line1 !== this.originalAddress.line1
        || this.address.line2 !== this.originalAddress.line2
        || this.address.city !== this.originalAddress.city
        || this.address.state !== this.originalAddress.state
        || this.address.zip !== this.originalAddress.zip
      );
    },
    hasContent() {
      return (
        this.address.line1.length > 0
        || this.address.city.length > 0
        || this.address.state.length > 0
        || this.address.zip.length > 0
      );
    },
    valid() {
      if (this.question.required) {
        return (
          this.address.line1.length > 0
          && this.address.city.length > 0
          && this.address.state.length > 0
          && this.address.zip.length > 0
        );
      }
      // If not required and empty, valid
      if (!this.hasContent) return true;
      // If partially filled, should complete it
      return (
        this.address.line1.length > 0
        && this.address.city.length > 0
        && this.address.state.length > 0
        && this.address.zip.length > 0
      );
    },
  },
  watch: {
    address: {
      deep: true,
      handler() {
        this.emit();
        if (this.valid) {
          this.error = false;
          this.errors = {
            line1: false, city: false, state: false, zip: false,
          };
        }
      },
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      const existing = this.existingAnswer.answer;
      if (typeof existing === 'object') {
        this.address = {
          line1: existing.line1 || '',
          line2: existing.line2 || '',
          city: existing.city || '',
          state: existing.state || '',
          zip: existing.zip || '',
        };
        this.originalAddress = { ...this.address };
      }
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    validateField(field) {
      if (this.question.required || this.hasContent) {
        this.errors[field] = this.address[field].length === 0;
      }
    },
    emit() {
      this.$emit('answer-changed', this.question, this.address, this.dirty, this.valid);
    },
    validate() {
      if (!this.valid) {
        this.error = true;
        // Show individual field errors
        if (this.question.required || this.hasContent) {
          this.errors.line1 = this.address.line1.length === 0;
          this.errors.city = this.address.city.length === 0;
          this.errors.state = this.address.state.length === 0;
          this.errors.zip = this.address.zip.length === 0;
        }
      } else {
        this.error = false;
        this.errors = {
          line1: false, city: false, state: false, zip: false,
        };
      }
    },
  },
};
</script>
